import { createEffect, createEvent, createStore, sample } from 'effector'
import { useUnit } from 'effector-react'
import React from 'react'
import { Form } from 'react-aria-components'
import { z } from 'zod'

import { css } from '@portal/styled-system/css'

import { LoginButtons } from '@/features/social-button'
import { $i18n, useI18n } from '@/shared/lib/i18n'
import { getLegalLinkHref } from '@/shared/lib/legal-link'
import { Button, Divider, Link } from '@/shared/ui'

import { AgreeTermsCheckboxField } from '../agree-terms-checkbox'
import { EmailInputField } from '../input-field'
import * as model from '../model'
import * as steps from '../steps'

export function SignupStepEmail() {
    const { i18n } = useI18n()

    const handleLoginClick = () => {
        steps.showStepLogin()
    }

    return (
        <React.Fragment>
            <div className={css({ color: 'gray-v2.700', textAlign: 'center', marginBottom: '2' })}>
                {i18n.TEXT_EXISTING_USER}&nbsp;
                <Button onPress={handleLoginClick} color="primary" variant="plain">
                    {i18n.LINK_LOGIN_HERE}
                </Button>
            </div>
            <LoginButtons showFbButton={false} />
            <Divider>{i18n.TEXT_OR}</Divider>
            <SignupEmail />
        </React.Fragment>
    )
}

const submitEmail = createEvent<string>()

const $error = createStore<string>('').reset(submitEmail).reset(model.$email)

const emailSchema = z.email()

const validateEmailFx = createEffect<string, string, z.ZodError>((email) => emailSchema.parse(email))

sample({
    clock: submitEmail,
    target: validateEmailFx,
})

sample({
    clock: validateEmailFx.failData,
    source: $i18n,
    filter: (_, error) => error.issues.length > 0,
    // NOTE: так как одно поле, то выводим generic ошибку
    fn: ({ i18n }) => i18n.EMAIL_CHANGE_ERROR__EMAIL_FORMAT,
    target: $error,
})

sample({
    clock: validateEmailFx.done,
    target: steps.showStepSignupPassword,
})

function SignupEmail() {
    const { i18n, lang } = useI18n()

    const errorMessage = useUnit($error)

    return (
        <Form
            method="post"
            onSubmit={(e) => {
                e.preventDefault()

                submitEmail(e.currentTarget.email.value)
            }}
            className={css({
                width: 'full',
                display: 'flex',
                flexDirection: 'column',
                gap: '3',
                paddingBottom: '2',
            })}
        >
            <EmailInputField
                placeholder={i18n.USER_EMAIL}
                autoComplete="username"
                isError={Boolean(errorMessage)}
                description={errorMessage}
            />
            <AgreeTermsCheckboxField name="userAgreeTerms">
                <span>
                    <span>{i18n.TEXT_LEGAL_YOU_AGREETO} </span>
                    <Link
                        href={getLegalLinkHref(lang, 'terms-of-service/')}
                        intent="cyan"
                        target="_blank"
                        rel="noreferrer"
                    >
                        {i18n.LINK_LEGAL_TERMS}
                    </Link>
                    <span>, </span>
                    <Link
                        href={getLegalLinkHref(lang, 'privacy-policy/')}
                        intent="cyan"
                        target="_blank"
                        rel="noreferrer"
                    >
                        {i18n.LINK_LEGAL_PRIVACY_TO}
                    </Link>
                    <span> {i18n.TEXT_AND} </span>
                    <Link href={getLegalLinkHref(lang, 'cookies/')} intent="cyan" target="_blank" rel="noreferrer">
                        {i18n.LINK_LEGAL_COOKIES}
                    </Link>
                    <span>.</span>
                </span>
            </AgreeTermsCheckboxField>
            <Button type="submit" color="cyan">
                {i18n.BUTTON_SIGNUP}
            </Button>
        </Form>
    )
}
